/**
 * Results export for bulk licence assignment (APD-13993): one CSV line per row the run touched.
 */
import { EMPTY_STRING } from '../../../../../React/Common/Constants/Separators'

/** One result line, in column order: Name, Username, Device, Agent, Partition, Licence, Action, Result. */
export type BulkResultLine = readonly [string, string, string, string, string, string, string, string]

const CSV_LINE_SEPARATOR: string = '\r\n'

/** Leading characters a spreadsheet reads as the start of a formula. */
const FORMULA_TRIGGER_PATTERN: RegExp = /^[=+\-@\t\r]/

/** Prefix that makes a spreadsheet show a formula-like value as text. */
const FORMULA_ESCAPE_PREFIX: string = "'"

/** Device and Agent columns: phone numbers that may legitimately start with "+", so never prefixed. */
const NUMBER_COLUMN_INDEXES: ReadonlySet<number> = new Set([2, 3])

/**
 * Escapes one CSV value: quoted, with inner quotes doubled, when it holds a comma, quote or newline.
 *
 * @param value Value to write.
 * @returns The escaped value.
 */
export const escapeCsvValue = (value: Nullable<string | number>): string => {
    const text: string = value === null || value === undefined ? EMPTY_STRING : String(value)

    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

/**
 * Stops a text value being run as a formula when the CSV is opened in a spreadsheet: a value that
 * starts with "=", "+", "-", "@", a tab or a carriage return is prefixed with an apostrophe. Names,
 * usernames and error texts come from telephony or user data, so they cannot be trusted.
 *
 * @param value Value to write.
 * @returns The value, safe to open in a spreadsheet.
 */
export const neutraliseCsvFormula = (value: Nullable<string | number>): Nullable<string | number> =>
    typeof value === 'string' && FORMULA_TRIGGER_PATTERN.test(value) ? `${FORMULA_ESCAPE_PREFIX}${value}` : value

/**
 * Builds the results CSV.
 *
 * @param header The 8 column headings.
 * @param lines Result lines.
 * @returns The CSV text.
 */
export const buildResultsCsv = (header: BulkResultLine, lines: readonly BulkResultLine[]): string =>
    [header, ...lines]
        .map((line: BulkResultLine): string =>
            line
                .map((value: string, index: number): string =>
                    escapeCsvValue(NUMBER_COLUMN_INDEXES.has(index) ? value : neutraliseCsvFormula(value))
                )
                .join(',')
        )
        .join(CSV_LINE_SEPARATOR)

/**
 * File name for a results export, stamped to the minute.
 *
 * @param now Export time.
 * @returns e.g. bulk-licence-results-2026-10-07-14-05.csv
 */
export const resultsCsvFileName = (now: Date): string =>
    `bulk-licence-results-${now.toISOString().slice(0, 16).replace(/[:T]/g, '-')}.csv`

/** UTF-8 byte order mark, so Excel reads non-ASCII names as UTF-8 rather than the system codepage. */
export const UTF8_BOM: string = '\uFEFF'

/** How long the blob URL outlives the click. Firefox and Safari can cancel a download whose URL is revoked at once. */
export const REVOKE_DELAY_MS: number = 1000

/**
 * Downloads CSV text as a UTF-8 file.
 *
 * @param csv CSV text.
 * @param fileName File name.
 */
export const downloadCsv = (csv: string, fileName: string): void => {
    const url: string = URL.createObjectURL(new Blob([UTF8_BOM, csv], { type: 'text/csv;charset=utf-8' }))
    const anchor: HTMLAnchorElement = document.createElement('a')

    anchor.href = url
    anchor.download = fileName
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    setTimeout((): void => URL.revokeObjectURL(url), REVOKE_DELAY_MS)
}
